import React from 'react';
import {
  FaBalanceScale,
  FaChartLine,
  FaFileInvoiceDollar,
  FaHandshake,
  FaHatCowboy,
  FaMapMarkedAlt,
  FaPlug,
  FaSeedling,
  FaStore,
  FaUsers,
  FaWarehouse,
} from 'react-icons/fa';
import ProductPageTemplate from '../../components/productPageTemplate';

const data = {
  theme: {
    accent: '#10A37F',
    accentSoft: '#41C59B',
    darkBg: '#103B34',
    darkBgSoft: '#146A56',
  },
  hero: {
    eyebrows: ['ERP para o campo', 'Fiscal e caixa rural', 'Feito para o produtor brasileiro'],
    accent: 'Agro',
    title: 'que acompanha produção, fiscal e gestão rural.',
    lead:
      'O ECOSIS Agro reúne emissão, controle financeiro, estoque, imóvel rural e leitura operacional em uma estrutura pensada para a rotina do agronegócio.',
    support:
      'A solução atende desde o pequeno produtor até operações coletivas, preservando organização fiscal e visão prática do que acontece no campo.',
    metrics: [
      'NFe, CTe e MDFe para rotina rural',
      'Financeiro, estoque e relatórios na mesma base',
      'Operação adaptada de produtor a cooperativa',
    ],
    primaryCta: { label: 'Ver módulos', href: '#modulos' },
    secondaryCta: { label: 'Falar com a equipe', href: '#contato-produto' },
    visualCards: [
      { label: 'Fiscal rural', value: 'Base organizada' },
      { label: 'Campo e gestão', value: 'Mesmo fluxo' },
    ],
    panelTitle: 'Painel da operação rural',
    panelItems: [
      {
        label: 'Emissão',
        title: 'Documentos do campo com menos atrito',
        meta: 'Notas e documentos logísticos reunidos para o dia a dia da produção e comercialização.',
      },
      {
        label: 'Financeiro',
        title: 'Contas e conciliação integradas',
        meta: 'Controle de pagar e receber, boletos e leitura bancária conectados à operação.',
      },
      {
        label: 'Gestão',
        title: 'Dados do imóvel e inteligência operacional',
        meta: 'Cadastros, relatórios e visão gerencial estruturados para o contexto rural.',
      },
    ],
  },
  intro: {
    tag: 'Visão geral',
    title: 'Uma operação rural mais organizada do fiscal ao acompanhamento diário.',
    lead:
      'A versão anterior já mostrava um produto voltado a rotina do produtor. Agora a página entrega essa proposta com hierarquia melhor e leitura menos dispersa.',
    cardTitle: 'Pensado para o agro brasileiro',
    cardText:
      'A solução foi posicionada para operações que precisam emitir, acompanhar caixa, organizar estoque e manter controle administrativo sem transformar o dia a dia em burocracia.',
    quickPoints: [
      'Emissão fiscal ligada ao contexto real do produtor e da cooperativa.',
      'Financeiro e estoque conectados à rotina operacional do campo.',
      'Estrutura que suporta desde produção individual até gestão coletiva.',
    ],
  },
  modulesSection: {
    tag: 'Módulos principais',
    title: 'Os principais blocos do Agro foram reorganizados para leitura mais clara.',
    lead:
      'O conteúdo abaixo preserva os recursos que já estavam presentes, mas em cards mais diretos e consistentes com o restante do site.',
  },
  modules: [
    {
      icon: FaFileInvoiceDollar,
      title: 'Documentos fiscais',
      description: 'Controle a emissão dos principais documentos utilizados na rotina comercial e logística do agro.',
      bullets: ['NFe', 'CTe', 'MDFe'],
    },
    {
      icon: FaBalanceScale,
      title: 'Fiscal e contábil',
      description: 'Apoio para operação de pessoa física, lucro real e lucro presumido com organização fiscal adequada.',
      bullets: ['Pessoa física e regimes empresariais', 'Livro caixa', 'Conformidade mais previsível'],
    },
    {
      icon: FaSeedling,
      title: 'Financeiro rural',
      description: 'Mantenha contas, boletos e conciliações ligados ao fluxo real de compra, venda e produção.',
      bullets: ['Contas a pagar e receber', 'Conciliação bancária', 'Boletos integrados ao banco'],
    },
    {
      icon: FaWarehouse,
      title: 'Estoque e produção',
      description: 'Acompanhe materiais, produção e etiquetagem com uma base mais organizada para a operação.',
      bullets: ['Controle de produção', 'Emissão de etiquetas', 'Leitura mais prática do estoque'],
    },
    {
      icon: FaChartLine,
      title: 'Gestão inteligente',
      description: 'Use dashboards, relatórios e gestão documental para enxergar melhor o andamento da operação.',
      bullets: ['Dashboards fáceis de interpretar', 'Relatórios operacionais', 'Assinador e gestão de documentos'],
    },
    {
      icon: FaMapMarkedAlt,
      title: 'Cadastro de imóvel rural',
      description: 'Estruture os dados da operação rural dentro do sistema para manter o controle administrativo coerente com o campo.',
      bullets: ['Cadastro específico do imóvel', 'Base preparada para rotina rural', 'Mais organização operacional'],
    },
    {
      icon: FaPlug,
      title: 'Integrações',
      description: 'Amplie a operação com recursos complementares conectados à rotina do produtor.',
      bullets: ['Atende à legislação dos estados', 'WhatsApp', 'Integração a fluxos de apoio'],
    },
    {
      icon: FaUsers,
      title: 'Agro Fiscal: gestão coletiva',
      description: 'Ideal para cooperativas e operações com múltiplos produtores, permitindo emissão independente com controle centralizado.',
      bullets: ['Cadastro de vários produtores', 'Emissões independentes', 'Pode ser contratado separadamente'],
    },
  ],
  operations: {
    tag: 'Onde encaixa melhor',
    title: 'O Agro foi pensado para operação individual e coletiva sem perder clareza fiscal.',
    lead:
      'Esse bloco concentra os perfis atendidos e os apoios laterais que faziam parte da página antiga, agora em um padrão mais profissional.',
    audienceTitle: 'Perfis atendidos',
    audienceText:
      'O sistema atende produtores, fazendas, cooperativas e associações que precisam unir controle operacional e obrigação fiscal em uma base confiável.',
    sectors: [
      { icon: FaHatCowboy, label: 'Produtor rural' },
      { icon: FaStore, label: 'Fazendas' },
      { icon: FaUsers, label: 'Cooperativas' },
      { icon: FaHandshake, label: 'Associações' },
      { icon: FaSeedling, label: 'Agronegócio em geral' },
    ],
    stat: {
      value: 'Brasil',
      text: 'Feito para o produtor brasileiro, atendendo do pequeno ao grande produtor com leitura operacional mais objetiva.',
    },
    cards: [
      {
        title: 'SEUPOS',
        text: 'A página anterior já citava o recurso como apoio adicional para ampliar a operação na ponta.',
        bullets: ['Complemento para frente de caixa', 'Integração ao ecossistema principal'],
      },
      {
        title: 'SEFAZ e averbação',
        text: 'Consultas fiscais e etapas complementares de averbação seguem como parte do suporte operacional.',
        bullets: ['Consulta de notas na SEFAZ', 'Averbação dentro do fluxo'],
      },
      {
        title: 'Painel do contador',
        text: 'Um apoio importante para reduzir atrito no fechamento e dar mais previsibilidade à operação fiscal.',
        bullets: ['Leitura mais direta para contabilidade', 'Melhor alinhamento com a gestão'],
      },
      {
        title: 'Escala operacional',
        text: 'A estrutura serve tanto ao produtor individual quanto à gestão coletiva, sem perder coerência de processo.',
        bullets: ['Pequeno ao grande produtor', 'Base única para evoluir a operação'],
      },
    ],
  },
  contact: {
    tag: 'Contato',
    title: 'Quer entender como o ECOSIS Agro encaixa no seu fluxo rural?',
    lead: 'A equipe pode avaliar sua operação atual e mapear a implantação mais coerente para o seu contexto.',
  },
};

function EcosisAgro() {
  return <ProductPageTemplate data={data} />;
}

export default EcosisAgro;
